import { getContract, JSONRpcProvider, ABIDataTypes, BitcoinAbiTypes, OP_NET_ABI } from 'opnet';
import { networks } from '@btc-vision/bitcoin';

const CONTRACT_ADDRESS = 'opt1sqryqgj3t8qsqzdnxu3hpqhyf59r7fmmhpyzjzrsn';
const EXPLORER_URL = 'https://opscan.org/';
const NETWORK_PARAM = 'op_testnet';
const RPC_URL = 'https://testnet.opnet.org';
const BTC_NETWORK = networks.opnetTestnet;
const MIN_FEE_RATE = 1;
const MAX_FEE_RATE = 888;
const DEFAULT_FEE_RATE = 1;
const SEND_TX_MAX_ATTEMPTS = 4;
const SEND_TX_RETRY_DELAY_MS = 4000;

const ACTIVATION_ABI = [
    {
        name: 'activate',
        inputs: [],
        outputs: [{ name: 'success', type: ABIDataTypes.BOOL }],
        type: BitcoinAbiTypes.Function,
    },
    {
        name: 'getActivationCount',
        inputs: [{ name: 'wallet', type: ABIDataTypes.ADDRESS }],
        outputs: [{ name: 'count', type: ABIDataTypes.UINT256 }],
        type: BitcoinAbiTypes.Function,
    },
    {
        name: 'getTotalActivations',
        inputs: [],
        outputs: [{ name: 'totalActivations', type: ABIDataTypes.UINT256 }],
        type: BitcoinAbiTypes.Function,
    },
    {
        name: 'getTotalUsers',
        inputs: [],
        outputs: [{ name: 'totalUsers', type: ABIDataTypes.UINT256 }],
        type: BitcoinAbiTypes.Function,
    },
    {
        name: 'getLeaderboardSize',
        inputs: [],
        outputs: [{ name: 'size', type: ABIDataTypes.UINT32 }],
        type: BitcoinAbiTypes.Function,
    },
    {
        name: 'getLeaderboardEntry',
        inputs: [{ name: 'index', type: ABIDataTypes.UINT32 }],
        outputs: [
            { name: 'wallet', type: ABIDataTypes.ADDRESS },
            { name: 'count', type: ABIDataTypes.UINT256 },
        ],
        type: BitcoinAbiTypes.Function,
    },
    ...OP_NET_ABI,
];

const addressCache = new Map();

export class ContractManager {
    constructor() {
        this.contractAddress = CONTRACT_ADDRESS;
        this.network = 'testnet';
        this.btcNetwork = BTC_NETWORK;
        this.provider = null;
        this._initProvider();
        this._loadData();
    }

    _toAddressString(value) {
        if (!value) return '';
        try {
            if (typeof value === 'string') return value;
            if (value.address) return String(value.address);
            return String(value);
        } catch {
            return '';
        }
    }

    _sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    async _sendWithRetry(simulation, txParams) {
        let lastError = null;

        for (let attempt = 1; attempt <= SEND_TX_MAX_ATTEMPTS; attempt++) {
            try {
                return await simulation.sendTransaction(txParams);
            } catch (error) {
                lastError = error;
                const message = String(error?.message || '');
                const isFundingCreationError = message.includes('Funding transaction not created');

                if (!isFundingCreationError || attempt === SEND_TX_MAX_ATTEMPTS) {
                    throw error;
                }

                console.warn(
                    `sendTransaction attempt ${attempt}/${SEND_TX_MAX_ATTEMPTS} failed: ${message}. Retrying...`,
                );
                await this._sleep(SEND_TX_RETRY_DELAY_MS);
            }
        }

        throw lastError || new Error('Funding transaction not created');
    }

    _clampFeeRate(value) {
        const num = Number(value);
        if (!Number.isFinite(num)) return DEFAULT_FEE_RATE;
        return Math.min(MAX_FEE_RATE, Math.max(MIN_FEE_RATE, Math.floor(num)));
    }

    _initProvider() {
        if (this.provider) return;
        this.provider = new JSONRpcProvider({
            url: RPC_URL,
            network: this.btcNetwork,
        });
    }

    async _resolveAddress(bech32Address, isContract = false) {
        if (!bech32Address) throw new Error('Address is required');
        if (addressCache.has(bech32Address)) return addressCache.get(bech32Address);

        this._initProvider();
        const resolved = await this.provider.getPublicKeyInfo(bech32Address, isContract);
        if (!resolved) {
            throw new Error(`Could not resolve address: ${bech32Address}`);
        }

        addressCache.set(bech32Address, resolved);
        return resolved;
    }

    async _getContract(senderAddress) {
        this._initProvider();

        const contractAddress = await this._resolveAddress(this.contractAddress, true);
        if (senderAddress) {
            try {
                const sender = await this._resolveAddress(senderAddress, false);
                return getContract(contractAddress, ACTIVATION_ABI, this.provider, this.btcNetwork, sender);
            } catch (error) {
                console.warn(
                    `Sender address is not indexed yet (${senderAddress}). Falling back to contract instance without sender.`,
                    error?.message || error,
                );
            }
        }

        return getContract(contractAddress, ACTIVATION_ABI, this.provider, this.btcNetwork);
    }

    _toNumber(value) {
        try {
            if (typeof value === 'number') return value;
            if (typeof value === 'bigint') return Number(value);
            if (value && typeof value.toString === 'function') return Number(value.toString());
            return 0;
        } catch {
            return 0;
        }
    }

    _loadData() {
        const saved = localStorage.getItem('btcActivationData');
        if (saved) {
            const data = JSON.parse(saved);
            this.data = {
                users: new Map(data.users || []),
                totalActivations: data.totalActivations || 0,
                totalUsers: data.totalUsers || 0,
            };
        } else {
            this.data = { users: new Map(), totalActivations: 0, totalUsers: 0 };
        }
    }

    _saveData() {
        localStorage.setItem('btcActivationData', JSON.stringify({
            users: Array.from(this.data.users.entries()),
            totalActivations: this.data.totalActivations,
            totalUsers: this.data.totalUsers,
        }));
    }

    async activate(address, walletProvider) {
        try {
            if (!address) throw new Error('Wallet address required');

            console.log('🔥 Активація Bitcoin...');
            console.log('📍 Контракт:', this.contractAddress);
            console.log('👤 Від:', address);

            const contract = await this._getContract(address);
            const simulation = await contract.activate();
            if (simulation?.revert) {
                throw new Error(`Contract revert: ${simulation.revert}`);
            }

            console.log('💰 Відправка контрактної транзакції через OP_WALLET...');
            const feeRate = this._clampFeeRate(DEFAULT_FEE_RATE);
            const receipt = await this._sendWithRetry(simulation, {
                refundTo: address,
                feeRate,
                maximumAllowedSatToSpend: 30000n,
                network: this.btcNetwork,
            });

            const txid = receipt?.transactionId || receipt?.txid || String(receipt);
            console.log('🎉 Транзакція відправлена! TXID:', txid);
            console.log(`🔍 Explorer: ${EXPLORER_URL}transactions/${txid}?network=${NETWORK_PARAM}`);

            // Local cache update for quick UI feedback (source of truth stays on-chain)
            const currentCount = this.data.users.get(address) || 0;
            if (currentCount === 0) this.data.totalUsers += 1;
            this.data.users.set(address, currentCount + 1);
            this.data.totalActivations += 1;
            this._saveData();

            // Save transaction
            this.saveTransaction({
                address,
                txid: txid,
                signature: null,
                data: { method: 'activate', contract: this.contractAddress },
                timestamp: Date.now(),
                activationNumber: currentCount + 1,
                network: this.network
            });

            return {
                success: true,
                txid: txid,
                activationCount: currentCount + 1,
                totalActivations: this.data.totalActivations,
            };

        } catch (error) {
            if (String(error?.message || '').includes('OP_NET: Invalid contract')) {
                error.message = `OP_NET: Invalid contract at ${this.contractAddress}. The address resolves, but no contract bytecode is available on RPC index.`;
            }
            console.error('❌ Помилка активації:', error);
            throw error;
        }
    }

    saveTransaction(tx) {
        const transactions = JSON.parse(localStorage.getItem('activationTransactions') || '[]');
        transactions.push(tx);
        localStorage.setItem('activationTransactions', JSON.stringify(transactions));
    }

    getTransactions(address) {
        const transactions = JSON.parse(localStorage.getItem('activationTransactions') || '[]');
        if (address) {
            return transactions.filter(tx => tx.address === address);
        }
        return transactions;
    }

    async getUserStats(address) {
        try {
            if (!address) return { activations: 0, status: this._getStatus(0) };

            const contract = await this._getContract();
            const walletAddress = await this._resolveAddress(address, false);
            const result = await contract.getActivationCount(walletAddress);
            if (result?.revert) throw new Error(result.revert);

            const activations = this._toNumber(result?.properties?.count ?? 0);
            return { activations, status: this._getStatus(activations) };
        } catch (error) {
            console.warn('getUserStats on-chain failed, fallback to local:', error?.message || error);
            const activations = this.data.users.get(address) || 0;
            return { activations, status: this._getStatus(activations) };
        }
    }

    async getGlobalStats() {
        try {
            const contract = await this._getContract();
            const [totalResult, usersResult] = await Promise.all([
                contract.getTotalActivations(),
                contract.getTotalUsers(),
            ]);

            if (totalResult?.revert) throw new Error(totalResult.revert);
            if (usersResult?.revert) throw new Error(usersResult.revert);

            return {
                totalActivations: this._toNumber(totalResult?.properties?.totalActivations ?? 0),
                totalUsers: this._toNumber(usersResult?.properties?.totalUsers ?? 0),
            };
        } catch (error) {
            console.warn('getGlobalStats on-chain failed, fallback to local:', error?.message || error);
            return {
                totalActivations: this.data.totalActivations,
                totalUsers: this.data.totalUsers,
            };
        }
    }

    async getLeaderboard() {
        try {
            const contract = await this._getContract();
            const sizeResult = await contract.getLeaderboardSize();
            if (sizeResult?.revert) throw new Error(sizeResult.revert);

            const size = Math.min(10, this._toNumber(sizeResult?.properties?.size ?? 0));
            const entries = [];

            for (let i = 0; i < size; i++) {
                const entry = await contract.getLeaderboardEntry(i);
                if (entry?.revert) continue;

                const wallet = this._toAddressString(entry?.properties?.wallet);
                const count = this._toNumber(entry?.properties?.count ?? 0);
                if (!wallet || count <= 0) continue;

                entries.push({ address: wallet, count });
            }

            if (entries.length > 0) return entries;
        } catch (error) {
            console.warn('getLeaderboard on-chain failed, fallback to local:', error?.message || error);
        }

        return Array.from(this.data.users.entries())
            .map(([address, count]) => ({ address, count }))
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
    }

    _getStatus(activations) {
        if (activations >= 100) return 'Bitcoin Legend 🔥';
        if (activations >= 50) return 'Bitcoin Master ⚡';
        if (activations >= 20) return 'Bitcoin Pro 💎';
        if (activations >= 10) return 'Bitcoin Enthusiast 🚀';
        return 'Bitcoin Activator';
    }
}
